import React, { useState, useEffect, useCallback } from 'react';
import { Property, SearchConsoleData, EnhancedSearchConsoleData, User } from '../types';
import { getProperties } from '../services/auth';
import TrialBanner from './TrialBanner';
import ReferralWidget from './ReferralWidget';
import { fetchSearchConsoleData, formatDateForAPI } from '../services/searchConsole';
import { analyzeInsights, InsightsSummary } from '../services/insights';
import { generateTopicClusters, TopicClustersResult } from '../services/topicClusters';
import { classifySearchIntent, getIntentIcon, SearchIntent } from '../services/searchIntent';
import { analyzeInternalLinkGaps, InternalLinkGapResult } from '../services/internalLinkGap';
import { CreateTodoRequest } from '../types/todo';
import { ContentGapAnalysis } from '../types/sitemap';
import { fetchSitemapData, analyzeSitemapContentGaps } from '../services/sitemapCrawler';
import InsightsPanel from './InsightsPanel';
import TopicClustersPanel from './TopicClustersPanel';
import SearchIntentPanel from './SearchIntentPanel';
import InternalLinkGapPanel from './InternalLinkGapPanel';
import TodoList from './TodoList';
import ContentGapsPanel from './ContentGapsPanel';
import HistoricalRankingsPanel from './HistoricalRankingsPanel';
import TrackedKeywordsPanel from './TrackedKeywordsPanel';
import ExplanationPanel from './ExplanationPanel';
import URLInspectorPanel from './URLInspectorPanel';
import SearchAppearancePanel from './SearchAppearancePanel';
import NonIndexedUrlsPanel from './NonIndexedUrlsPanel';

interface DashboardProps {
  userId: number;
  user: User;
  onNavigateToAccount?: () => void;
}

type SortField = 'query' | 'page' | 'clicks' | 'impressions' | 'ctr' | 'position';
type SortDirection = 'asc' | 'desc';
type PositionRange = '1-10' | '11-20' | '21+';

const Dashboard: React.FC<DashboardProps> = ({ userId, user, onNavigateToAccount }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [data, setData] = useState<SearchConsoleData[]>([]);
  const [enhancedData, setEnhancedData] = useState<EnhancedSearchConsoleData[]>([]);
  const [filteredData, setFilteredData] = useState<EnhancedSearchConsoleData[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const [urlFilter, setUrlFilter] = useState<string>('');
  const [queryPage, setQueryPage] = useState<number>(1);
  const ROWS_PER_PAGE = 200;
  const GSC_BATCH_SIZE = 500;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMoreGSC, setIsLoadingMoreGSC] = useState(false);
  const [hasMoreGSCData, setHasMoreGSCData] = useState(false);
  const [gscStartRow, setGscStartRow] = useState(0);
  const [gscBaseRequest, setGscBaseRequest] = useState<any>(null);
  const [searchAppearanceData, setSearchAppearanceData] = useState<any[]>([]);
  const [featuredSnippetQueries, setFeaturedSnippetQueries] = useState<any[]>([]);
  const [aiOverviewQueries, setAiOverviewQueries] = useState<any[]>([]);
  const [isLoadingAppearance, setIsLoadingAppearance] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('clicks');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [positionRange, setPositionRange] = useState<PositionRange>('1-10');
  const [positionData, setPositionData] = useState<EnhancedSearchConsoleData[]>([]);
  const [positionKeyword, setPositionKeyword] = useState<string>('');
  const [filteredPositionData, setFilteredPositionData] = useState<EnhancedSearchConsoleData[]>([]);
  const [positionSortField, setPositionSortField] = useState<SortField>('position');
  const [positionSortDirection, setPositionSortDirection] = useState<SortDirection>('asc');
  const [questionsSortKey, setQuestionsSortKey] = useState<'query' | 'clicks' | 'impressions' | 'ctr' | 'position'>('clicks');
  const [questionsSortDir, setQuestionsSortDir] = useState<'asc' | 'desc'>('desc');
  const [locationFilter, setLocationFilter] = useState<string>('gbr'); // Default to UK
  
  // Insights state
  const [insights, setInsights] = useState<InsightsSummary>({
    quickWins: [],
    strikingDistance: [],
    ctrAnomalies: [],
    featuredSnippets: [],
    cannibalization: [],
    longTail: [],
    localIntent: [],
    total: 0
  });

  // Topic Clusters state
  const [topicClusters, setTopicClusters] = useState<TopicClustersResult | null>(null);
  const [clusteringMethod, setClusteringMethod] = useState<'word-based' | 'semantic-similarity' | 'page-based'>('word-based');
  const [isLoadingClusters, setIsLoadingClusters] = useState(false);
  
  // Search intent filtering state
  const [selectedIntent, setSelectedIntent] = useState<SearchIntent | null>(null);
  
  // Internal Link Gap analysis state
  const [internalLinkGaps, setInternalLinkGaps] = useState<InternalLinkGapResult | null>(null);
  const [isLoadingGaps, setIsLoadingGaps] = useState(false);
  
  // To-Do List state and function
  const [userEmail, setUserEmail] = useState<string>('');
  const [addedTodos, setAddedTodos] = useState<Set<string>>(new Set());
  const [todoRefreshTrigger, setTodoRefreshTrigger] = useState(0);

  // Sitemap content analysis state
  const [contentGaps, setContentGaps] = useState<ContentGapAnalysis | null>(null);
  const [hasSitemapData, setHasSitemapData] = useState(false);
  
  // Tracked Keywords state
  interface TrackedKeywordWithMetrics {
    id: number;
    query: string;
    propertyUrl?: string;
    latestMetrics?: {
      currentPosition?: number;
      clicks?: number;
      impressions?: number;
      ctr?: number;
      trend?: 'up' | 'down' | 'stable' | 'new';
    };
    createdAt?: string;
    updatedAt?: string;
  }
  
  const [trackedKeywords, setTrackedKeywords] = useState<TrackedKeywordWithMetrics[]>([]);
  const [isLoadingTrackedKeywords, setIsLoadingTrackedKeywords] = useState(false);
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Direct addTodo function
  const addTodo = useCallback(async (todoData: Omit<CreateTodoRequest, 'userEmail' | 'propertyUrl'>) => {
    if (!userEmail || !selectedProperty) {
      throw new Error('User email or property not available');
    }

    try {
      const response = await fetch('/api/todos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail,
          propertyUrl: selectedProperty,
          ...todoData
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const newTodo = await response.json();
      
      // Trigger refresh in TodoList
      setTodoRefreshTrigger(prev => prev + 1);
      
      // Track the added todo for visual feedback
      const todoKey = `${todoData.title}-${todoData.sourcePanel || 'general'}`;
      setAddedTodos(prev => new Set(prev).add(todoKey));
      
      return newTodo;
    } catch (err) {
      alert('Failed to add task to your to-do list');
      throw err;
    }
  }, [userEmail, selectedProperty]);

  // Tracked Keywords API functions
  const fetchTrackedKeywords = useCallback(async () => {
    if (!selectedProperty) return;
    
    setIsLoadingTrackedKeywords(true);
    try {
      const response = await fetch(`/api/tracked-keywords?propertyUrl=${encodeURIComponent(selectedProperty)}`);
      if (response.ok) {
        const keywords = await response.json();
        // Ensure each keyword has the propertyUrl for proper scoping
        const keywordsWithProperty = keywords.map((keyword: any) => ({
          ...keyword,
          propertyUrl: selectedProperty
        }));
        setTrackedKeywords(keywordsWithProperty);
      } else {
        console.error('Failed to fetch tracked keywords');
      }
    } catch (error) {
      console.error('Error fetching tracked keywords:', error);
    } finally {
      setIsLoadingTrackedKeywords(false);
    }
  }, [selectedProperty]);

  const trackKeyword = useCallback(async (query: string) => {
    if (!selectedProperty) return;
    
    try {
      const response = await fetch('/api/tracked-keywords', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propertyUrl: selectedProperty,
          query: query
        }),
      });

      if (response.ok) {
        // Refresh tracked keywords list
        await fetchTrackedKeywords();
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to track keyword: ${errorText}`);
      }
    } catch (error) {
      console.error('Error tracking keyword:', error);
      throw error;
    }
  }, [selectedProperty, fetchTrackedKeywords]);

  const untrackKeyword = useCallback(async (keywordId: number) => {
    try {
      const response = await fetch(`/api/tracked-keywords/${keywordId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Refresh tracked keywords list
        await fetchTrackedKeywords();
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to untrack keyword: ${errorText}`);
      }
    } catch (error) {
      console.error('Error untracking keyword:', error);
      throw error;
    }
  }, [fetchTrackedKeywords]);

  // Update tracked keywords with latest metrics from Search Console data
  const updateTrackedKeywordsMetrics = useCallback(async () => {
    if (!enhancedData.length || !trackedKeywords.length) return;

    const updatedKeywords = trackedKeywords.map(trackedKeyword => {
      // Find matching data in enhancedData
      const matchingData = enhancedData.find(item => 
        item.query?.toLowerCase() === trackedKeyword.query?.toLowerCase()
      );

      if (matchingData) {
        // Calculate trend based on position (simplified logic for now)
        let trend: 'up' | 'down' | 'stable' | 'new' = 'new';
        if (trackedKeyword.latestMetrics?.currentPosition) {
          const previousPosition = trackedKeyword.latestMetrics.currentPosition;
          const currentPosition = matchingData.position;
          
          if (currentPosition < previousPosition - 2) {
            trend = 'up'; // Position improved (lower number is better)
          } else if (currentPosition > previousPosition + 2) {
            trend = 'down'; // Position declined
          } else {
            trend = 'stable'; // Position stayed roughly the same
          }
        }

        // Update the tracked keyword with latest metrics
        return {
          ...trackedKeyword,
          latestMetrics: {
            currentPosition: matchingData.position,
            clicks: matchingData.clicks,
            impressions: matchingData.impressions,
            ctr: matchingData.ctr,
            trend: trend
          }
        };
      }

      return trackedKeyword;
    });

    // Update local state
    setTrackedKeywords(updatedKeywords);

    // Optionally update backend with latest metrics
    try {
      for (const keyword of updatedKeywords) {
        if (keyword.latestMetrics && Object.keys(keyword.latestMetrics).length > 0) {
          // Call backend API to update metrics
          await fetch(`/api/tracked-keywords/${keyword.id}/metrics`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              latestMetrics: keyword.latestMetrics
            }),
          });
        }
      }
    } catch (error) {
      console.error('Error updating tracked keywords metrics in backend:', error);
    }
  }, [enhancedData, trackedKeywords]);

  // Extract user email from user prop
  useEffect(() => {
    if (user?.email) {
      setUserEmail(user.email);
    }
  }, [user?.email]);

  // Fetch tracked keywords when property changes
  useEffect(() => {
    if (selectedProperty) {
      fetchTrackedKeywords();
    }
  }, [selectedProperty, fetchTrackedKeywords]);

  // Update tracked keywords metrics when enhanced data changes
  useEffect(() => {
    if (enhancedData.length > 0 && trackedKeywords.length > 0) {
      updateTrackedKeywordsMetrics();
    }
  }, [enhancedData, updateTrackedKeywordsMetrics]);

  // Analyze insights whenever enhanced data changes
  useEffect(() => {
    if (enhancedData.length > 0) {
      const insightsResult = analyzeInsights(enhancedData);
      setInsights(insightsResult);
    }
  }, [enhancedData]);

  // Generate internal link gap analysis when enhanced data changes
  useEffect(() => {
    if (enhancedData.length > 0) {
      setIsLoadingGaps(true);
      // Use a small delay to allow UI to update with loading state
      setTimeout(() => {
        try {
          const gapResult = analyzeInternalLinkGaps(enhancedData);
          setInternalLinkGaps(gapResult);
        } catch (error) {
          console.error('Error analyzing internal link gaps:', error);
          setInternalLinkGaps({
            totalPages: 0,
            totalPotentialAnchors: 0,
            pageAnalyses: [],
            summary: {
              highPriorityOpportunities: 0,
              mediumPriorityOpportunities: 0,
              lowPriorityOpportunities: 0
            }
          });
        } finally {
          setIsLoadingGaps(false);
        }
      }, 300);
    } else {
      setInternalLinkGaps(null);
    }
  }, [enhancedData]);

  // Generate topic clusters when data or clustering method changes
  useEffect(() => {
    if (enhancedData.length > 0) {
      setIsLoadingClusters(true);
      // Use a small delay to allow UI to update with loading state
      setTimeout(() => {
        try {
          const clustersResult = generateTopicClusters(enhancedData, clusteringMethod, 10);
          setTopicClusters(clustersResult);
        } catch (error) {
          console.error('Error generating topic clusters:', error);
          setTopicClusters({
            clusters: [],
            method: clusteringMethod,
            totalClusters: 0,
            totalKeywordsInClusters: 0
          });
        } finally {
          setIsLoadingClusters(false);
        }
      }, 300);
    } else {
      setTopicClusters(null);
    }
  }, [enhancedData, clusteringMethod]);




  useEffect(() => {
    // Set default date range - 7 days for free users, 30 days for paid/admin
    const end = new Date();
    end.setDate(end.getDate() - 3); // GSC has 3-day delay
    const start = new Date();
    const daysBack = (user.plan === 'free' && !user.isAdmin) ? 10 : 33; // 7 days or 30 days
    start.setDate(start.getDate() - daysBack);
    
    setEndDate(formatDateForAPI(end));
    setStartDate(formatDateForAPI(start));

    // Fetch properties
    const fetchProps = async () => {
      try {
        const props = await getProperties();
        setProperties(props);
        if (props.length > 0) {
          // For free users (non-admins), use their selected property if they have one, otherwise leave empty
          if (user.plan === 'free' && !user.isAdmin) {
            if (user.selectedGscProperty) {
              setSelectedProperty(user.selectedGscProperty);
            } else {
              setSelectedProperty(''); // Don't auto-select, force explicit choice
            }
          } else {
            // Paid users and admins get auto-selected to first property
            setSelectedProperty(props[0].siteUrl);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch properties');
      }
    };


    fetchProps();
  }, [user.id]);

  useEffect(() => {
    // Filter and sort data when keyword, URL filter, intent filter, or sort changes
    let filtered = enhancedData;
    
    // Apply search intent filter
    if (selectedIntent) {
      filtered = filtered.filter(item => {
        const intentAnalysis = classifySearchIntent(item.query);
        return intentAnalysis.intent === selectedIntent;
      });
    }
    
    // Apply keyword filter (supports both single keywords and pipe-separated cluster keywords)
    if (keyword.trim()) {
      const keywordLower = keyword.toLowerCase();
      
      // Check if this is a cluster filter (contains pipe separator)
      if (keywordLower.includes('|')) {
        // Split by pipe and check if query matches any of the keywords
        const clusterKeywords = keywordLower.split('|').map(k => k.trim()).filter(k => k.length > 0);
        filtered = filtered.filter(item => 
          item.query && clusterKeywords.some(clusterKeyword => 
            item.query.toLowerCase().includes(clusterKeyword)
          )
        );
      } else {
        // Single keyword filter
        filtered = filtered.filter(item => 
          item.query && item.query.toLowerCase().includes(keywordLower)
        );
      }
    }
    
    // Apply URL filter
    if (urlFilter.trim()) {
      filtered = filtered.filter(item => 
        item.page && item.page.toLowerCase().includes(urlFilter.toLowerCase())
      );
    }
    
    // Free plan: limit to top 15 positions only
    if (user.plan === 'free' && !user.isAdmin) {
      filtered = filtered.filter(item => item.position <= 15);
    }

    // Apply sorting
    const sorted = sortEnhancedData(filtered, sortField, sortDirection);
    setFilteredData(sorted);
    // Don't recalculate metrics - we use accurate aggregate totals from API
  }, [keyword, urlFilter, enhancedData, sortField, sortDirection, selectedIntent, user.plan, user.isAdmin]);

  useEffect(() => {
    // Filter data by position range
    const filtered = filterByPositionRangeEnhanced(enhancedData, positionRange);
    setPositionData(filtered);
  }, [enhancedData, positionRange]);

  useEffect(() => {
    setQueryPage(1);
  }, [keyword, urlFilter]);







  useEffect(() => {
    // Filter position data by keyword and intent (supports both single keywords and pipe-separated cluster keywords)
    let filtered = positionData;
    
    // Apply search intent filter
    if (selectedIntent) {
      filtered = filtered.filter(item => {
        const intentAnalysis = classifySearchIntent(item.query);
        return intentAnalysis.intent === selectedIntent;
      });
    }
    
    if (positionKeyword.trim()) {
      const keywordLower = positionKeyword.toLowerCase();
      
      // Check if this is a cluster filter (contains pipe separator)
      if (keywordLower.includes('|')) {
        // Split by pipe and check if query matches any of the keywords
        const clusterKeywords = keywordLower.split('|').map(k => k.trim()).filter(k => k.length > 0);
        filtered = filtered.filter(item => 
          item.query && clusterKeywords.some(clusterKeyword => 
            item.query.toLowerCase().includes(clusterKeyword)
          )
        );
      } else {
        // Single keyword filter
        filtered = filtered.filter(item => 
          item.query && item.query.toLowerCase().includes(keywordLower)
        );
      }
    }
    
    // Apply sorting
    const sorted = sortEnhancedData(filtered, positionSortField, positionSortDirection);
    setFilteredPositionData(sorted);
  }, [positionData, positionKeyword, positionSortField, positionSortDirection, selectedIntent]);


  const sortEnhancedData = (dataSet: EnhancedSearchConsoleData[], field: SortField, direction: SortDirection): EnhancedSearchConsoleData[] => {
    return [...dataSet].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (field) {
        case 'query':
          aValue = (a.query || '').toLowerCase();
          bValue = (b.query || '').toLowerCase();
          break;
        case 'page':
          aValue = (a.page || '').toLowerCase();
          bValue = (b.page || '').toLowerCase();
          break;
        case 'clicks':
          aValue = a.clicks;
          bValue = b.clicks;
          break;
        case 'impressions':
          aValue = a.impressions;
          bValue = b.impressions;
          break;
        case 'ctr':
          aValue = a.ctr;
          bValue = b.ctr;
          break;
        case 'position':
          aValue = a.position;
          bValue = b.position;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (field: SortField) => {
    setQueryPage(1);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(['clicks', 'impressions', 'ctr'].includes(field) ? 'desc' : 'asc');
    }
  };

  const handlePositionSort = (field: SortField) => {
    if (positionSortField === field) {
      // Toggle direction if same field
      setPositionSortDirection(positionSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending for numeric fields, ascending for text
      setPositionSortField(field);
      setPositionSortDirection(['clicks', 'impressions', 'ctr'].includes(field) ? 'desc' : 'asc');
    }
  };


  const filterByPositionRangeEnhanced = (dataSet: EnhancedSearchConsoleData[], range: PositionRange): EnhancedSearchConsoleData[] => {
    return dataSet.filter(item => {
      const pos = item.position;
      switch (range) {
        case '1-10':
          return pos >= 1 && pos <= 10;
        case '11-20':
          return pos >= 11 && pos <= 20;
        case '21+':
          return pos > 20;
        default:
          return true;
      }
    }).sort((a, b) => a.position - b.position); // Sort by position ascending
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <span className="inline-flex flex-col ml-1">
          <svg className="w-2 h-2 text-gray-400" fill="currentColor" viewBox="0 0 8 8">
            <path d="M0 4l4-4 4 4H0z"/>
          </svg>
          <svg className="w-2 h-2 text-gray-400 -mt-1" fill="currentColor" viewBox="0 0 8 8">
            <path d="M8 4L4 8 0 4h8z"/>
          </svg>
        </span>
      );
    }
    
    if (sortDirection === 'asc') {
      return (
        <svg className="inline w-3 h-3 ml-1 text-gray-700" fill="currentColor" viewBox="0 0 8 8">
          <path d="M0 4l4-4 4 4H0z"/>
        </svg>
      );
    } else {
      return (
        <svg className="inline w-3 h-3 ml-1 text-gray-700" fill="currentColor" viewBox="0 0 8 8">
          <path d="M8 4L4 8 0 4h8z"/>
        </svg>
      );
    }
  };

  const getPositionSortIcon = (field: SortField) => {
    if (positionSortField !== field) {
      return (
        <span className="inline-flex flex-col ml-1">
          <svg className="w-2 h-2 text-gray-400" fill="currentColor" viewBox="0 0 8 8">
            <path d="M0 4l4-4 4 4H0z"/>
          </svg>
          <svg className="w-2 h-2 text-gray-400 -mt-1" fill="currentColor" viewBox="0 0 8 8">
            <path d="M8 4L4 8 0 4h8z"/>
          </svg>
        </span>
      );
    }
    
    if (positionSortDirection === 'asc') {
      return (
        <svg className="inline w-3 h-3 ml-1 text-gray-700" fill="currentColor" viewBox="0 0 8 8">
          <path d="M0 4l4-4 4 4H0z"/>
        </svg>
      );
    } else {
      return (
        <svg className="inline w-3 h-3 ml-1 text-gray-700" fill="currentColor" viewBox="0 0 8 8">
          <path d="M8 4L4 8 0 4h8z"/>
        </svg>
      );
    }
  };

  // Helper function to truncate queries and add tooltip functionality
  const renderTruncatedQuery = (query: string) => {
    const words = query.split(' ');
    const shouldTruncate = words.length > 6;
    
    if (!shouldTruncate) {
      return <span>{query}</span>;
    }
    
    const truncatedQuery = words.slice(0, 6).join(' ') + '...';
    
    return (
      <span 
        title={query}
        className="cursor-help"
      >
        {truncatedQuery}
      </span>
    );
  };

  const handlePropertyChange = async (newPropertyUrl: string) => {
    // Free users (non-admins) can only use one property
    if (user.plan === 'free' && !user.isAdmin) {
      if (!user.selectedGscProperty) {
        // First time selecting - save it
        try {
          const response = await fetch('/api/user/select-property', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ propertyUrl: newPropertyUrl }),
          });
          
          if (response.ok) {
            setSelectedProperty(newPropertyUrl);
            // Reload page to refresh user data with selected property
            window.location.reload();
          } else {
            const errorData = await response.json();
            alert(errorData.message || 'Failed to select property');
          }
        } catch (error) {
          console.error('Error saving selected property:', error);
        }
      } else if (user.selectedGscProperty !== newPropertyUrl) {
        // Trying to switch properties - show upgrade message
        alert('Free plan users can only use one GSC property. Upgrade to Pro to access all your properties.');
        return;
      } else {
        // Same property, allow it
        setSelectedProperty(newPropertyUrl);
      }
    } else {
      // Paid users can switch freely
      setSelectedProperty(newPropertyUrl);
    }
  };

  const fetchData = async () => {
    if (!selectedProperty || !startDate || !endDate) return;

    // Check subscription access - allow both free and paid users
    // Free plan users have limited features, paid users have full access
    if (user.subscriptionStatus === 'expired') {
      setError('Your subscription has expired. Please renew to continue accessing Search Console data.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setHasMoreGSCData(false);
    setGscStartRow(0);
    setGscBaseRequest(null);

    try {
      // Fetch aggregate totals without dimensions for accurate metrics
      const aggregateRequest: any = {
        startDate,
        endDate,
        dimensions: [], // No dimensions = aggregate totals only
        rowLimit: 1,
        dataState: 'final', // Use FINAL for stable numbers that match GSC UI
      };

      // Add location filter if not worldwide
      if (locationFilter !== 'worldwide') {
        aggregateRequest.dimensionFilterGroups = [{
          filters: [{
            dimension: 'country',
            operator: 'equals',
            expression: locationFilter
          }]
        }];
      }

      const aggregateData = await fetchSearchConsoleData(selectedProperty, aggregateRequest);
      
      // Log aggregate metrics for debugging (no longer displayed in UI)
      if (aggregateData && aggregateData.length > 0) {
        const totals = aggregateData[0];
        console.log('Aggregate metrics:', totals);
      }
      
      // Fetch Search Console data with dimensions for detailed analysis
      // Use batched fetching: load first batch quickly, allow loading more on demand
      const batchSize = (user.plan === 'free' && !user.isAdmin) ? 25000 : GSC_BATCH_SIZE;
      const searchConsoleRequest: any = {
        startDate,
        endDate,
        dimensions: ['query', 'page'],
        rowLimit: batchSize,
        startRow: 0,
        dataState: 'final', // Use FINAL for stable numbers that match GSC UI
      };

      // Add location filter if not worldwide
      if (locationFilter !== 'worldwide') {
        searchConsoleRequest.dimensionFilterGroups = [{
          filters: [{
            dimension: 'country',
            operator: 'equals',
            expression: locationFilter
          }]
        }];
      }

      const searchData = await fetchSearchConsoleData(selectedProperty, searchConsoleRequest);

      console.log('Fetched GSC data:', searchData.length, 'rows');
      setData(searchData);

      // If we got a full batch, there may be more data available
      const moreAvailable = user.plan !== 'free' && searchData.length === batchSize;
      setHasMoreGSCData(moreAvailable);
      setGscStartRow(searchData.length);
      setGscBaseRequest(searchConsoleRequest);

      // Convert to EnhancedSearchConsoleData
      const combinedData: EnhancedSearchConsoleData[] = searchData.map(row => ({ ...row } as EnhancedSearchConsoleData));

      console.log('Combined data result:', combinedData.length, 'rows');

      setEnhancedData(combinedData);
      setFilteredData(combinedData);
      // Metrics already set from aggregate query above - don't recalculate from filtered data
      
      // Fetch sitemap data for content gap analysis
      if (selectedProperty) {
        try {
          console.log('Fetching sitemap for:', selectedProperty);
          const sitemapResult = await fetchSitemapData(selectedProperty);
          console.log('Fetched sitemap data:', sitemapResult.total, 'URLs from', sitemapResult.sitemapUrl);
          setHasSitemapData(true);
          
          // Analyze content gaps
          const gaps = analyzeSitemapContentGaps(combinedData, sitemapResult);
          setContentGaps(gaps);
          console.log('Content gap analysis:', gaps);
        } catch (sitemapError) {
          console.log('No sitemap data available:', sitemapError);
          setContentGaps(null);
          setHasSitemapData(false);
        }
      }
      
      // Insights will be analyzed via useEffect
      
      // Fetch search appearance overview in the background (non-blocking)
      setIsLoadingAppearance(true);
      fetch('/api/gsc/search-appearance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ siteUrl: selectedProperty, startDate, endDate }),
      }).then(r => r.json()).then(d => {
        const rows = (d.rows || []).map((row: any) => ({
          searchAppearance: row.keys?.[0] || '',
          clicks: row.clicks || 0,
          impressions: row.impressions || 0,
          ctr: row.ctr || 0,
          position: row.position || 0,
        }));
        setSearchAppearanceData(rows);
      }).catch(e => console.log('Search appearance fetch failed:', e))
        .finally(() => setIsLoadingAppearance(false));

      // Try to activate referral if this is a new user who was referred
      try {
        const referralResponse = await fetch('/api/referral/activate', {
          method: 'POST',
          credentials: 'include'
        });
        
        if (referralResponse.ok) {
          const referralResult = await referralResponse.json();
          if (referralResult.rewarded) {
            console.log('Referral activated! Referrer earned a bonus property slot.');
          }
        }
      } catch (referralError) {
        // Silently fail - don't disrupt the user experience if referral activation fails
        console.log('Referral activation check completed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreFromGSC = async () => {
    if (!selectedProperty || !gscBaseRequest || isLoadingMoreGSC) return;

    setIsLoadingMoreGSC(true);
    try {
      const nextRequest = {
        ...gscBaseRequest,
        startRow: gscStartRow,
        rowLimit: GSC_BATCH_SIZE,
      };

      const newRows = await fetchSearchConsoleData(selectedProperty, nextRequest);
      console.log('Loaded more GSC rows:', newRows.length, 'starting from', gscStartRow);

      const newEnhanced: EnhancedSearchConsoleData[] = newRows.map(row => ({ ...row } as EnhancedSearchConsoleData));
      const combined = [...enhancedData, ...newEnhanced];

      setData(prev => [...prev, ...newRows]);
      setEnhancedData(combined);

      const newStartRow = gscStartRow + newRows.length;
      setGscStartRow(newStartRow);
      setHasMoreGSCData(newRows.length === GSC_BATCH_SIZE);
    } catch (err) {
      console.error('Failed to load more GSC data:', err);
    } finally {
      setIsLoadingMoreGSC(false);
    }
  };

  const downloadCSV = () => {
    if (filteredData.length === 0) return;

    // Check if user is on free plan (admins bypass this restriction)
    if (user.plan === 'free' && !user.isAdmin) {
      setShowUpgradeModal(true);
      return;
    }

    const headers = ['Query', 'URL', 'Clicks', 'Impressions', 'CTR (%)', 'Position'];
    const csvContent = [
      headers.join(','),
      ...filteredData.map(row => [
        `"${row.query}"`,
        `"${row.page || ''}"`,
        row.clicks,
        row.impressions,
        (row.ctr * 100).toFixed(2),
        row.position.toFixed(1)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `seo-analysis-${keyword || 'all'}-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadInsightsCSV = () => {
    if (insights.total === 0) return;

    // Check if user is on free plan (admins bypass this restriction)
    if (user.plan === 'free' && !user.isAdmin) {
      setShowUpgradeModal(true);
      return;
    }

    // Collect all insights from all categories
    const allInsights = [
      ...insights.quickWins.map(insight => ({ ...insight, category: 'Quick Wins' })),
      ...insights.strikingDistance.map(insight => ({ ...insight, category: 'Striking Distance' })),
      ...insights.ctrAnomalies.map(insight => ({ ...insight, category: 'CTR Issues' })),
      ...insights.featuredSnippets.map(insight => ({ ...insight, category: 'Possible Featured Snippets' })),
      ...insights.cannibalization.map(insight => ({ ...insight, category: 'Keyword Conflicts' })),
      ...insights.longTail.map(insight => ({ ...insight, category: 'Long-tail' })),
      ...insights.localIntent.map(insight => ({ ...insight, category: 'Local SEO' }))
    ];

    const headers = ['Category', 'Query', 'URL', 'Description', 'Action', 'Severity', 'Position', 'Clicks', 'Impressions', 'CTR (%)'];
    const csvContent = [
      headers.join(','),
      ...allInsights.map(insight => [
        `"${insight.category}"`,
        `"${insight.query}"`,
        `"${insight.page || ''}"`,
        `"${insight.description}"`,
        `"${insight.action}"`,
        `"${insight.severity}"`,
        insight.position.toFixed(1),
        insight.clicks,
        insight.impressions,
        (insight.ctr * 100).toFixed(2)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `seo-opportunities-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle filtering by cluster keywords
  const handleFilterByCluster = (clusterKeywords: string[]) => {
    // Create a search pattern that matches any of the cluster keywords
    const keywordPattern = clusterKeywords.join('|');
    setKeyword(keywordPattern);
    setUrlFilter(''); // Clear URL filter for better focus
    
    // Scroll to Query Performance table
    setTimeout(() => {
      const element = document.querySelector('[data-section="query-performance"]');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Handle clustering method change
  const handleClusteringMethodChange = (method: 'word-based' | 'semantic-similarity' | 'page-based') => {
    setClusteringMethod(method);
  };
  
  // Handle intent filtering
  const handleIntentFilter = (intent: SearchIntent | null) => {
    setSelectedIntent(intent);
    // Clear other filters for better focus when intent is selected
    if (intent) {
      setKeyword('');
      setUrlFilter('');
      setPositionKeyword('');
    }
  };

  // Prepare chart data (top 10 by clicks)
  const chartData = filteredData
    .slice(0, 20)
    .map(item => ({
      query: item.query.length > 20 ? item.query.substring(0, 20) + '...' : item.query,
      clicks: item.clicks,
      impressions: item.impressions,
      ctr: parseFloat((item.ctr * 100).toFixed(2)),
      position: parseFloat(item.position.toFixed(1)),
    }));

  // Check if access is restricted - both free and paid users have access
  const hasDataAccess = user.subscriptionStatus !== 'expired';

  return (
    <div className="space-y-6">
      {/* Trial Banner */}
      <TrialBanner user={user} />
      
      {/* Access Restriction Message */}
      {!hasDataAccess && (
        <div className="bg-red-50 border border-red-200 rounded-md p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Trial Expired</h3>
              <div className="mt-2 text-sm text-red-700">
                Your trial period has ended. Please upgrade to Professional to continue accessing Search Console data and SEO insights.
              </div>
              <div className="mt-4">
                <button
                  onClick={() => onNavigateToAccount && onNavigateToAccount()}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Dashboard Content - Only show if user has access */}
      {hasDataAccess && (
        <React.Fragment>
          {/* Warning for free users who haven't selected a property */}
          {user.plan === 'free' && !user.isAdmin && !user.selectedGscProperty && properties.length > 0 && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">⚠️ Free Plan: Choose Your Property Carefully</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      <strong>Important:</strong> On the free plan, you can only select <strong>one Google Search Console property</strong>. Once selected, you'll be locked to that property unless you upgrade to Pro.
                    </p>
                    <p className="mt-2">
                      Please make sure you choose the correct property before proceeding. If you manage multiple sites, consider upgrading to Pro for unlimited property access.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Property and Date Selection */}
          <div className="bg-white rounded-lg shadow-lg p-6 anchor-target" id="configuration">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Data Configuration</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-1">GSC Property</label>
            <select
              value={selectedProperty}
              onChange={(e) => handlePropertyChange(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {/* Show placeholder for free users who haven't selected yet */}
              {user.plan === 'free' && !user.isAdmin && !user.selectedGscProperty && (
                <option value="" disabled>Select your GSC property...</option>
              )}
              {properties.map(prop => {
                const isLocked = user.plan === 'free' && !user.isAdmin && user.selectedGscProperty && prop.siteUrl !== user.selectedGscProperty;
                return (
                  <option 
                    key={prop.siteUrl} 
                    value={prop.siteUrl}
                    style={isLocked ? { textDecoration: 'line-through', color: '#999' } : undefined}
                  >
                    {prop.siteUrl} {isLocked ? '(Upgrade for access)' : ''}
                  </option>
                );
              })}
            </select>
            <div className="h-6 mt-1">
              {selectedProperty && data.length > 0 && (
                <a
                  href={`https://search.google.com/search-console/performance/search-analytics?resource_id=${encodeURIComponent(selectedProperty)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 inline-block"
                >
                  → Open in Search Console
                </a>
              )}
            </div>
          </div>
          
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="gbr">United Kingdom</option>
              <option value="usa">United States</option>
              <option value="can">Canada</option>
              <option value="aus">Australia</option>
              <option value="deu">Germany</option>
              <option value="fra">France</option>
              <option value="esp">Spain</option>
              <option value="ita">Italy</option>
              <option value="nld">Netherlands</option>
              <option value="worldwide">Worldwide</option>
            </select>
            <div className="h-6 mt-1"></div>
          </div>
          
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { if (!(user.plan === 'free' && !user.isAdmin)) setStartDate(e.target.value); }}
              disabled={user.plan === 'free' && !user.isAdmin}
              className={`w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${user.plan === 'free' && !user.isAdmin ? 'bg-gray-100 cursor-not-allowed text-gray-400' : ''}`}
            />
            {user.plan === 'free' && !user.isAdmin ? (
              <p className="text-xs text-orange-600 mt-1">
                🔒 7 days only — <button onClick={() => setShowUpgradeModal(true)} className="underline font-medium hover:text-orange-800">Upgrade for 16 months</button>
              </p>
            ) : <div className="h-6 mt-1"></div>}
          </div>
          
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { if (!(user.plan === 'free' && !user.isAdmin)) setEndDate(e.target.value); }}
              disabled={user.plan === 'free' && !user.isAdmin}
              className={`w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${user.plan === 'free' && !user.isAdmin ? 'bg-gray-100 cursor-not-allowed text-gray-400' : ''}`}
            />
            <div className="h-6 mt-1"></div>
          </div>
          
          <div className="flex flex-col justify-end">
            <button
              onClick={fetchData}
              disabled={isLoading || !selectedProperty}
              className="w-full bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md transition duration-200"
            >
              {isLoading ? 'Loading...' : 'Fetch GSC Data'}
            </button>
            <div className="h-6 mt-1"></div>
          </div>
        </div>
      </div>


      {/* To-Do List */}
      {selectedProperty && userEmail && (
        <TodoList 
          userEmail={userEmail}
          propertyUrl={selectedProperty}
          refreshTrigger={todoRefreshTrigger}
        />
      )}


      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}


      {enhancedData.length > 0 && (
        <React.Fragment>
          {/* SEO Insights Panel */}
          <div id="insights" className="anchor-target">
            <InsightsPanel 
            insights={insights} 
            onApplyFilter={(query, positionRange) => {
              setKeyword(query);
              setUrlFilter(''); // Clear URL filter for better focus
              // Set position range based on insight type
              if (positionRange) {
                setPositionRange(positionRange as PositionRange);
              }
              // Scroll to Query Performance table
              setTimeout(() => {
                const element = document.querySelector('[data-section="query-performance"]');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            onDownloadCSV={downloadInsightsCSV}
            onAddTodo={userEmail && selectedProperty ? addTodo : undefined}
            addedTodos={addedTodos}
          />
          
          {/* Explanation Panel for SEO Insights */}
          <ExplanationPanel
            title="What am I looking at? - AI-Powered SEO Insights"
            content="This AI-powered analysis identifies your biggest SEO opportunities by analyzing your search performance data:

Insight Types:
• High Impression, Low CTR: Keywords with lots of visibility but poor click rates - optimize titles and descriptions
• Low Position, High Potential: Keywords where small ranking improvements could bring significant traffic
• Content Gaps: Topics your competitors rank for but you don't have content about
• Technical Issues: Pages with crawling, indexing, or performance problems

Each insight includes:
• Priority Level: How impactful this opportunity could be
• Specific Recommendations: Actionable steps to improve performance
• Expected Impact: Estimated traffic or ranking improvements
• Difficulty Level: How much effort this will require"
            actionGuidance="Focus your SEO efforts for maximum impact:
• Start with High Priority insights - these offer the biggest potential gains
• Click 'Apply Filter' to see the specific keywords behind each insight
• Use the 'Add to Tasks' button to track your progress on each opportunity
• Work on Quick Wins first to build momentum before tackling complex issues
• Monitor changes after implementing recommendations to measure success"
            className="mt-4"
          />
          </div>

          {/* Topic Clusters Panel */}
          <div id="topic-clusters" className="anchor-target">
            <TopicClustersPanel
            clustersResult={topicClusters}
            onMethodChange={handleClusteringMethodChange}
            onFilterByCluster={handleFilterByCluster}
            isLoading={isLoadingClusters}
            propertyUrl={selectedProperty}
          />
          
          {/* Explanation Panel for Topic Clusters */}
          <ExplanationPanel
            title="What am I looking at? - Topic Clusters"
            content="Topic clustering groups your keywords by semantic similarity to help you understand content themes and user intent:

How It Works:
• Keywords are analyzed for semantic relationships and meaning
• Similar keywords are grouped into clusters representing topics
• Each cluster shows the total traffic potential and keyword count

Clustering Methods:
• Semantic Clustering: Groups by meaning and intent (recommended)
• Keyword-based: Groups by shared words and phrases
• Custom: Advanced options for specific needs

Each cluster shows:
• Total Keywords: Number of related keywords in this topic
• Total Clicks: Combined traffic from all keywords in the cluster
• Average Position: How well you rank for this topic overall
• Topic Label: The main theme or intent of the cluster"
            actionGuidance="Use topic clusters to improve your content strategy:
• Identify content gaps: Clusters with many keywords but low clicks need better content
• Content consolidation: Multiple low-traffic pages about the same topic can be combined
• New content ideas: Large clusters suggest topics worth creating comprehensive content about
• Click a cluster to filter your data and see all related keywords
• Focus on clusters with high impression but low click potential for quick wins
• Use clusters to plan your content calendar around profitable topics"
            className="mt-4"
          />
          </div>
          
          {/* Internal Link Gap Analysis Panel */}
          <div id="link-gaps" className="anchor-target">
            <InternalLinkGapPanel
            gapResult={internalLinkGaps}
            isLoading={isLoadingGaps}
            user={user}
            onAddTodo={userEmail && selectedProperty ? addTodo : undefined}
            addedTodos={addedTodos}
          />
          
          {/* Explanation Panel for Internal Link Gap Analysis */}
          <ExplanationPanel
            title="What am I looking at? - Internal Link Gap Analysis"
            content="This analysis identifies opportunities to improve your internal linking structure by analyzing your search performance data:

How It Works:
• Groups keywords by the pages they rank for
• Identifies pages with multiple ranking keywords that could benefit from internal links
• Suggests potential anchor text based on your actual search queries
• Prioritizes opportunities based on traffic and ranking potential

What You'll See:
• Target Page: The page that could benefit from more internal links
• Keywords Count: Number of different keywords this page ranks for
• Potential Anchors: Suggested anchor text based on your ranking keywords
• Priority Level: High/Medium/Low based on traffic potential and current position

Priority Scoring:
• High: Pages with good traffic potential but room for ranking improvement
• Medium: Pages with moderate traffic and link opportunities
• Low: Pages with limited immediate impact but still worth considering"
            actionGuidance="Improve your internal linking strategy:
• Start with High Priority opportunities - these offer the biggest potential gains
• Use suggested anchor texts when linking from other relevant pages
• Link from pages with related content to maximize relevance
• Don't over-optimize - keep anchor text natural and varied
• Focus on pages ranking on page 2-3 (positions 11-30) - these can improve fastest
• Monitor ranking changes after adding internal links to measure impact
• Use the 'Add to Tasks' feature to track your internal linking efforts"
            className="mt-4"
          />
          </div>

          {/* Content Gaps Analysis Panel */}
          {hasSitemapData && (
            <div id="content-gaps" className="anchor-target">
              <ContentGapsPanel
                contentGaps={contentGaps}
                isVisible={hasSitemapData}
                onAddTodo={userEmail && selectedProperty ? addTodo : undefined}
              />
            </div>
          )}

          {/* Historical Rankings Panel */}
          <div id="historical-rankings" className="anchor-target">
            <HistoricalRankingsPanel
              property={selectedProperty}
              locationFilter={locationFilter}
              user={user}
              onAddTodo={userEmail && selectedProperty ? addTodo : undefined}
              trackedKeywords={trackedKeywords}
              onTrackKeyword={trackKeyword}
              onUntrackKeyword={untrackKeyword}
            />
          </div>

          {/* Tracked Keywords Panel */}
          <div id="tracked-keywords" className="anchor-target">
            <TrackedKeywordsPanel
              trackedKeywords={trackedKeywords.map(tk => ({
                id: tk.id,
                query: tk.query,
                propertyUrl: selectedProperty,
                latestMetrics: tk.latestMetrics || {},
                createdAt: tk.createdAt || new Date().toISOString(),
                updatedAt: tk.updatedAt || new Date().toISOString()
              }))}
              isLoading={isLoadingTrackedKeywords}
              onUntrackKeyword={untrackKeyword}
              onRefreshMetrics={updateTrackedKeywordsMetrics}
            />
          </div>

          {/* Search Intent Analysis Panel */}
          <div id="search-intent" className="anchor-target">
            <SearchIntentPanel
            data={enhancedData}
            onFilterByIntent={handleIntentFilter}
          />
          </div>

          {/* Featured Snippets & AI Overviews */}
          <div id="rich-features" className="anchor-target">
            <SearchAppearancePanel
              data={searchAppearanceData}
              featuredSnippetQueries={featuredSnippetQueries}
              aiOverviewQueries={aiOverviewQueries}
              isLoading={isLoadingAppearance}
            />
          </div>

          {/* AI Searches Section */}
          {(() => {
            const isAiLong = (q: string) => {
              if (!q) return false;
              const words = q.trim().split(/\s+/);
              return words.length >= 8 || q.length >= 50;
            };
            const aiQueries = enhancedData
              .filter(row => isAiLong(row.query))
              .sort((a, b) => b.clicks - a.clicks);
            const totalAiClicks = aiQueries.reduce((sum, r) => sum + r.clicks, 0);
            const totalAiImpressions = aiQueries.reduce((sum, r) => sum + r.impressions, 0);

            return (
              <div id="ai-searches" className="anchor-target bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-2xl">🤖</span>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">AI Searches</h2>
                      <p className="text-sm text-gray-500">Long queries (8+ words) likely copy-pasted from AI tools like ChatGPT</p>
                    </div>
                  </div>
                </div>

                {aiQueries.length === 0 ? (
                  <div className="px-6 py-10 text-center text-gray-400">No AI-likely queries found in the current dataset.</div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 divide-x divide-gray-200 border-b border-gray-200">
                      <div className="px-6 py-4">
                        <p className="text-sm text-gray-500">Potential AI queries</p>
                        <p className="text-2xl font-bold text-purple-600">{aiQueries.length.toLocaleString()}</p>
                      </div>
                      <div className="px-6 py-4">
                        <p className="text-sm text-gray-500">Total clicks</p>
                        <p className="text-2xl font-bold text-gray-900">{totalAiClicks.toLocaleString()}</p>
                      </div>
                      <div className="px-6 py-4">
                        <p className="text-sm text-gray-500">Total impressions</p>
                        <p className="text-2xl font-bold text-gray-900">{totalAiImpressions.toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="overflow-x-auto overflow-y-auto max-h-[36rem]">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Query</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ranking URL</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CTR</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {aiQueries.slice(0, 200).map((row, index) => {
                            const todoKey = `Optimise for: ${row.query}-AI Searches`;
                            const isAdded = addedTodos.has(todoKey);
                            return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-3 text-sm text-gray-900 max-w-md">
                                <div className="flex items-center gap-2">
                                  <span className="truncate">{row.query}</span>
                                  <a
                                    href={`https://www.google.co.uk/search?q=${encodeURIComponent(row.query)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors flex-shrink-0"
                                  >SERP</a>
                                </div>
                              </td>
                              <td className="px-6 py-3 text-sm text-blue-600 max-w-xs truncate">
                                {row.page ? (
                                  <a href={row.page} target="_blank" rel="noopener noreferrer" title={row.page} className="hover:underline">
                                    {row.page.replace(/^https?:\/\//, '')}
                                  </a>
                                ) : <span className="text-gray-400">—</span>}
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-900">{row.clicks.toLocaleString()}</td>
                              <td className="px-6 py-3 text-sm text-gray-900">{row.impressions.toLocaleString()}</td>
                              <td className="px-6 py-3 text-sm text-gray-900">{(row.ctr * 100).toFixed(2)}%</td>
                              <td className="px-6 py-3 text-sm text-gray-900">{row.position.toFixed(1)}</td>
                              <td className="px-6 py-3 text-sm">
                                {userEmail && selectedProperty && (
                                  <button
                                    onClick={() => addTodo({ title: `Optimise for: ${row.query}`, description: `AI search query — ${row.clicks} clicks, position ${row.position.toFixed(1)}`, priority: row.position <= 10 ? 'high' : 'medium', sourcePanel: 'AI Searches' })}
                                    disabled={isAdded}
                                    className={`text-xs px-2 py-1 rounded transition-colors ${isAdded ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                                  >
                                    {isAdded ? 'Added' : '+ To-Do'}
                                  </button>
                                )}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="px-6 py-3 bg-purple-50 border-t border-purple-100">
                      <p className="text-xs text-purple-700">💡 These long queries suggest users copy-pasted a question from an AI tool into Google to verify or find a source. Optimising for these could capture growing AI-referred traffic.</p>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* Questions Section */}
          {(() => {
            const QUESTION_WORDS = ['who', 'what', 'where', 'when', 'why', 'how', 'is', 'are', 'can', 'does', 'will', 'should', 'which', 'do', 'was', 'were'];
            const isQuestion = (q: string) => {
              if (!q) return false;
              const firstWord = q.trim().split(/\s+/)[0].toLowerCase();
              return QUESTION_WORDS.includes(firstWord);
            };
            const toggleQSort = (key: typeof questionsSortKey) => {
              if (questionsSortKey === key) {
                setQuestionsSortDir(d => d === 'asc' ? 'desc' : 'asc');
              } else {
                setQuestionsSortKey(key);
                setQuestionsSortDir(key === 'query' ? 'asc' : 'desc');
              }
            };
            const qSortIcon = (key: typeof questionsSortKey) => {
              if (questionsSortKey !== key) return <span className="text-gray-300 ml-1">↕</span>;
              return <span className="text-blue-500 ml-1">{questionsSortDir === 'asc' ? '↑' : '↓'}</span>;
            };
            const questionQueries = enhancedData
              .filter(row => isQuestion(row.query))
              .sort((a, b) => {
                const dir = questionsSortDir === 'asc' ? 1 : -1;
                if (questionsSortKey === 'query') return dir * a.query.localeCompare(b.query);
                if (questionsSortKey === 'clicks') return dir * (a.clicks - b.clicks);
                if (questionsSortKey === 'impressions') return dir * (a.impressions - b.impressions);
                if (questionsSortKey === 'ctr') return dir * (a.ctr - b.ctr);
                if (questionsSortKey === 'position') return dir * (a.position - b.position);
                return 0;
              });
            const totalQClicks = questionQueries.reduce((sum, r) => sum + r.clicks, 0);
            const totalQImpressions = questionQueries.reduce((sum, r) => sum + r.impressions, 0);

            const downloadQuestionsCSV = () => {
              const headers = ['Query', 'Starts With', 'Ranking URL', 'Clicks', 'Impressions', 'CTR (%)', 'Position'];
              const rows = questionQueries.map(r => {
                const firstWord = r.query.trim().split(/\s+/)[0] || '';
                return [
                  `"${r.query.replace(/"/g, '""')}"`,
                  firstWord,
                  `"${(r.page || '').replace(/"/g, '""')}"`,
                  r.clicks,
                  r.impressions,
                  (r.ctr * 100).toFixed(2),
                  r.position.toFixed(1)
                ].join(',');
              });
              const csv = [headers.join(','), ...rows].join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'questions.csv';
              a.click();
              URL.revokeObjectURL(url);
            };

            return (
              <div id="questions" className="anchor-target bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">❓</span>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Questions</h2>
                        <p className="text-sm text-gray-500">Queries starting with a question word — great candidates for FAQ content and featured snippets</p>
                      </div>
                    </div>
                    {questionQueries.length > 0 && (
                      <button
                        onClick={downloadQuestionsCSV}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                        title="Download as CSV"
                      >
                        ⬇ CSV
                      </button>
                    )}
                  </div>
                </div>

                {questionQueries.length === 0 ? (
                  <div className="px-6 py-10 text-center text-gray-400">No question-based queries found in the current dataset.</div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 divide-x divide-gray-200 border-b border-gray-200">
                      <div className="px-6 py-4">
                        <p className="text-sm text-gray-500">Question queries</p>
                        <p className="text-2xl font-bold text-green-600">{questionQueries.length.toLocaleString()}</p>
                      </div>
                      <div className="px-6 py-4">
                        <p className="text-sm text-gray-500">Total clicks</p>
                        <p className="text-2xl font-bold text-gray-900">{totalQClicks.toLocaleString()}</p>
                      </div>
                      <div className="px-6 py-4">
                        <p className="text-sm text-gray-500">Total impressions</p>
                        <p className="text-2xl font-bold text-gray-900">{totalQImpressions.toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="overflow-x-auto overflow-y-auto max-h-[36rem]">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleQSort('query')}>Query {qSortIcon('query')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ranking URL</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Starts with</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleQSort('clicks')}>Clicks {qSortIcon('clicks')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleQSort('impressions')}>Impressions {qSortIcon('impressions')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleQSort('ctr')}>CTR {qSortIcon('ctr')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleQSort('position')}>Position {qSortIcon('position')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {questionQueries.slice(0, 200).map((row, index) => {
                            const firstWord = row.query.trim().split(/\s+/)[0] || '';
                            const todoKey = `Answer: ${row.query}-Questions`;
                            const isAdded = addedTodos.has(todoKey);
                            return (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-6 py-3 text-sm text-gray-900 max-w-md">
                                  <div className="flex items-center gap-2">
                                    <span className="truncate">{row.query}</span>
                                    <a
                                      href={`https://www.google.co.uk/search?q=${encodeURIComponent(row.query)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors flex-shrink-0"
                                    >SERP</a>
                                  </div>
                                </td>
                                <td className="px-6 py-3 text-sm text-blue-600 max-w-xs truncate">
                                  {row.page ? (
                                    <a href={row.page} target="_blank" rel="noopener noreferrer" title={row.page} className="hover:underline">
                                      {row.page.replace(/^https?:\/\//, '')}
                                    </a>
                                  ) : <span className="text-gray-400">—</span>}
                                </td>
                                <td className="px-6 py-3 text-sm">
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium capitalize">{firstWord}</span>
                                </td>
                                <td className="px-6 py-3 text-sm text-gray-900">{row.clicks.toLocaleString()}</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{row.impressions.toLocaleString()}</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{(row.ctr * 100).toFixed(2)}%</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{row.position.toFixed(1)}</td>
                                <td className="px-6 py-3 text-sm">
                                  {userEmail && selectedProperty && (
                                    <button
                                      onClick={() => addTodo({ title: `Answer: ${row.query}`, description: `Question query — ${row.clicks} clicks, position ${row.position.toFixed(1)}. Consider FAQ content or featured snippet optimisation.`, priority: row.position <= 10 ? 'high' : 'medium', sourcePanel: 'Questions' })}
                                      disabled={isAdded}
                                      className={`text-xs px-2 py-1 rounded transition-colors ${isAdded ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                                    >
                                      {isAdded ? 'Added' : '+ To-Do'}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="px-6 py-3 bg-green-50 border-t border-green-100">
                      <p className="text-xs text-green-700">💡 Question queries are prime targets for FAQ sections, How-to guides, and featured snippet optimisation. Pages that directly answer these questions tend to earn higher CTR and position improvements.</p>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* Data Table - Query Performance */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden anchor-target" data-section="query-performance" id="query-performance">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-xl font-bold text-gray-900">Query Performance ({filteredData.length} results)</h2>
                <button
                  onClick={downloadCSV}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download CSV
                </button>
              </div>

              {user.plan === 'free' && !user.isAdmin && (
                <div className="flex items-center gap-2 text-sm bg-orange-50 border border-orange-200 rounded-md px-3 py-2 mb-3">
                  <span>🔒</span>
                  <span className="text-orange-800">Free plan shows <strong>top 15 positions only</strong> and <strong>last 7 days</strong> of data.</span>
                  <button onClick={() => setShowUpgradeModal(true)} className="ml-auto text-orange-600 underline font-medium hover:text-orange-800 whitespace-nowrap">Upgrade to Pro</button>
                </div>
              )}
              
              <div className="space-y-3">
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="Filter by keyword (e.g., 'react', 'seo', 'marketing')"
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={urlFilter}
                  onChange={(e) => setUrlFilter(e.target.value)}
                  placeholder="Filter by URL (e.g., '/blog', '/products', 'example.com')"
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            
            <div className="overflow-x-auto overflow-y-auto max-h-[48rem]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('query')}
                    >
                      Query {getSortIcon('query')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('page')}
                    >
                      URL {getSortIcon('page')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('clicks')}
                    >
                      Clicks {getSortIcon('clicks')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('impressions')}
                    >
                      Impressions {getSortIcon('impressions')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('ctr')}
                    >
                      CTR {getSortIcon('ctr')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('position')}
                    >
                      Position {getSortIcon('position')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Page Views
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bounce Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Time on Page
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.slice(0, queryPage * ROWS_PER_PAGE).map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2 max-w-xs">
                          {(() => {
                            const intentAnalysis = classifySearchIntent(row.query);
                            return (
                              <span 
                                className="text-lg flex-shrink-0" 
                                title={`${intentAnalysis.intent.charAt(0).toUpperCase() + intentAnalysis.intent.slice(1)} intent (${Math.round(intentAnalysis.confidence * 100)}% confidence)`}
                              >
                                {getIntentIcon(intentAnalysis.intent)}
                              </span>
                            );
                          })()}
                          <span className="text-gray-900 truncate flex-1 min-w-0">{row.query}</span>
                          <a 
                            href={`https://www.google.co.uk/search?q=${encodeURIComponent(row.query)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors flex-shrink-0"
                            title={`Search Google for: ${row.query}`}
                          >
                            SERP
                          </a>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-blue-600 max-w-sm truncate">
                        <a href={row.page} target="_blank" rel="noopener noreferrer" title={row.page}>
                          {row.page ? row.page.replace(/^https?:\/\//, '') : 'N/A'}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{row.clicks.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{row.impressions.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{(row.ctr * 100).toFixed(2)}%</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{row.position.toFixed(1)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        -
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        -
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        -
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredData.length > queryPage * ROWS_PER_PAGE && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                <span className="text-sm text-gray-600">
                  Showing {Math.min(queryPage * ROWS_PER_PAGE, filteredData.length).toLocaleString()} of {filteredData.length.toLocaleString()} results
                </span>
                <button
                  onClick={() => setQueryPage(p => p + 1)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md text-sm font-medium transition duration-200"
                >
                  Load more
                </button>
              </div>
            )}
            {filteredData.length > 0 && filteredData.length <= queryPage * ROWS_PER_PAGE && filteredData.length > ROWS_PER_PAGE && (
              <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
                <span className="text-sm text-gray-600">
                  Showing all {filteredData.length.toLocaleString()} results
                </span>
              </div>
            )}
            {hasMoreGSCData && (
              <div className="px-6 py-4 border-t border-blue-100 bg-blue-50 flex items-center justify-between">
                <span className="text-sm text-blue-800">
                  <strong>{enhancedData.length.toLocaleString()} rows loaded</strong> — there's more data available from Google.
                </span>
                <button
                  onClick={loadMoreFromGSC}
                  disabled={isLoadingMoreGSC}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-5 py-2 rounded-md text-sm font-medium transition duration-200 flex items-center gap-2"
                >
                  {isLoadingMoreGSC ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Loading from Google...
                    </>
                  ) : 'Load next 500 from Google'}
                </button>
              </div>
            )}
          </div>

          {/* Charts - Keyword Analysis */}
          {chartData.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Keyword Analysis</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {chartData.slice(0, 4).map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      {item.query}
                    </h3>
                    <p className="text-2xl font-bold text-blue-600">{item.clicks}</p>
                    <p className="text-sm text-gray-600">{item.ctr}% CTR</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Position Analysis Table */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden anchor-target" id="position-analysis">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Position Analysis ({filteredPositionData.length} results)</h2>
                <select 
                  value={positionRange} 
                  onChange={(e) => setPositionRange(e.target.value as PositionRange)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1-10">Positions 1-10</option>
                  <option value="11-20">Positions 11-20</option>
                  <option value="21+">Positions 21+</option>
                </select>
              </div>
              
              <input
                type="text"
                value={positionKeyword}
                onChange={(e) => setPositionKeyword(e.target.value)}
                placeholder="Filter by keyword (e.g., 'react', 'seo', 'marketing')"
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />
              
              {/* Position Range Summary */}
              {filteredPositionData.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <h3 className="text-lg font-bold text-blue-600">
                      {filteredPositionData.reduce((sum, item) => sum + item.clicks, 0).toLocaleString()}
                    </h3>
                    <p className="text-blue-800 text-sm font-medium">Total Clicks</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <h3 className="text-lg font-bold text-green-600">
                      {filteredPositionData.reduce((sum, item) => sum + item.impressions, 0).toLocaleString()}
                    </h3>
                    <p className="text-green-800 text-sm font-medium">Total Impressions</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <h3 className="text-lg font-bold text-purple-600">
                      {((filteredPositionData.reduce((sum, item) => sum + item.clicks, 0) / filteredPositionData.reduce((sum, item) => sum + item.impressions, 0)) * 100).toFixed(2)}%
                    </h3>
                    <p className="text-purple-800 text-sm font-medium">Average CTR</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3">
                    <h3 className="text-lg font-bold text-orange-600">
                      {(filteredPositionData.reduce((sum, item) => sum + item.position, 0) / filteredPositionData.length).toFixed(1)}
                    </h3>
                    <p className="text-orange-800 text-sm font-medium">Average Position</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="overflow-x-auto overflow-y-auto max-h-[48rem]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handlePositionSort('position')}
                    >
                      Position {getPositionSortIcon('position')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handlePositionSort('query')}
                    >
                      Query {getPositionSortIcon('query')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handlePositionSort('page')}
                    >
                      URL {getPositionSortIcon('page')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handlePositionSort('clicks')}
                    >
                      Clicks {getPositionSortIcon('clicks')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handlePositionSort('impressions')}
                    >
                      Impressions {getPositionSortIcon('impressions')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handlePositionSort('ctr')}
                    >
                      CTR {getPositionSortIcon('ctr')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Page Views
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bounce Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Time on Page
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPositionData.length > 0 ? filteredPositionData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {Math.round(row.position * 10) / 10}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          {(() => {
                            const intentAnalysis = classifySearchIntent(row.query);
                            return (
                              <span 
                                className="text-lg flex-shrink-0" 
                                title={`${intentAnalysis.intent.charAt(0).toUpperCase() + intentAnalysis.intent.slice(1)} intent (${Math.round(intentAnalysis.confidence * 100)}% confidence)`}
                              >
                                {getIntentIcon(intentAnalysis.intent)}
                              </span>
                            );
                          })()}
                          <span className="text-gray-900 flex-1 min-w-0">{renderTruncatedQuery(row.query)}</span>
                          <a 
                            href={`https://www.google.co.uk/search?q=${encodeURIComponent(row.query)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors flex-shrink-0"
                            title={`Search Google for: ${row.query}`}
                          >
                            SERP
                          </a>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 hover:text-blue-800">
                        <a href={row.page} target="_blank" rel="noopener noreferrer">
                          {row.page ? (new URL(row.page).pathname === '/' ? new URL(row.page).hostname : new URL(row.page).pathname) : 'N/A'}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.clicks.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.impressions.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(row.ctr * 100).toFixed(2)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        -
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        -
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        -
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                        {positionKeyword ? `No results found for "${positionKeyword}" in positions ${positionRange}` : `No data available for positions ${positionRange}`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* URL Inspector */}
          <div id="url-inspector" className="anchor-target">
            <URLInspectorPanel siteUrl={selectedProperty} />
          </div>
          <div id="non-indexed-urls" className="anchor-target mt-6">
            <NonIndexedUrlsPanel key={selectedProperty} siteUrl={selectedProperty} />
          </div>
        </React.Fragment>
      )}
        </React.Fragment>
      )}

      {/* Upgrade Modal for CSV Export */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Upgrade to Pro Plan</h3>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                CSV exports are available on the Pro plan. Export your data to Excel or Google Sheets for deeper analysis and reporting.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Pro Plan includes:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    CSV exports for all data
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Unlimited GSC properties
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    16 months of historical data
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    AI-powered content briefs
                  </li>
                </ul>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">£15/month</p>
              <p className="text-sm text-gray-600">Including VAT</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Maybe Later
              </button>
              <a
                href="https://buy.stripe.com/5kA9CV7tF7iw8IEcMP"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-center font-medium"
              >
                Upgrade Now
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Alvey Ad Banner */}
      <div className="mt-8 mx-auto rounded-2xl overflow-hidden shadow-lg" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0d2b4e 50%, #0a3d6b 100%)' }}>
        <div className="flex flex-col md:flex-row items-center gap-6 px-8 py-7">
          <img
            src="/alvey-logo-white.png"
            alt="Alvey by Searchworthy"
            className="h-16 md:h-20 w-auto flex-shrink-0"
          />
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
              <span className="text-xs font-semibold uppercase tracking-widest text-blue-300">Invite Only · Now Available</span>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
              Your WordPress site, fully managed by AI
            </h3>
            <p className="text-sm text-blue-100 leading-relaxed max-w-2xl">
              Alvey connects directly to your WordPress site, understands your content, fixes errors, identifies content gaps and writes blogs in <em>your</em> tone of voice — then pushes them live with Yoast meta, descriptions, excerpts and contextually correct internal links already set. Better rankings, more AI mentions.
            </p>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-4 text-xs text-blue-200">
              <span className="flex items-center gap-1">✓ Connects to WordPress</span>
              <span className="flex items-center gap-1">✓ Writes in your tone</span>
              <span className="flex items-center gap-1">✓ Auto Yoast meta &amp; excerpts</span>
              <span className="flex items-center gap-1">✓ Content gap detection</span>
              <span className="flex items-center gap-1">✓ Contextual internal links</span>
              <span className="flex items-center gap-1">✓ Improves AI mentions</span>
            </div>
          </div>
          <div className="flex-shrink-0">
            <a
              href="mailto:hello@searchworthy.co.uk?subject=Tell%20me%20more%20about%20Alvey"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all shadow-lg"
              style={{ background: 'linear-gradient(135deg, #1a9fd4 0%, #0d6fa8 100%)', color: '#ffffff' }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
            >
              Request an Invite
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </a>
            <p className="text-xs text-blue-300 text-center mt-2">hello@searchworthy.co.uk</p>
          </div>
        </div>
      </div>

      {/* Referral Widget for Free Users - Bottom of Page */}
      <ReferralWidget user={user} />
    </div>
  );
};

export default Dashboard;